import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';

const TIME_SLOTS = [
  '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM',
  '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'
];

export function GutterCleaningTab() {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime || !address.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate booking submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success(`Gutter cleaning booked for ${format(selectedDate, 'PPP')} at ${selectedTime}`);
      
      // Reset form
      setSelectedDate(undefined);
      setSelectedTime('');
      setAddress('');
      setNotes('');
    } catch (error) {
      toast.error('Failed to book appointment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Gutter Cleaning Booking
          </CardTitle>
          <CardDescription>
            Schedule your gutter cleaning service
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Calendar Section */}
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold mb-3 block">Select Date</Label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date() || date.getDay() === 0}
                  className="rounded-md border"
                />
                {selectedDate && (
                  <p className="mt-3 text-sm text-muted-foreground">
                    Selected: {format(selectedDate, 'PPP')}
                  </p>
                )}
              </div>
            </div>

            {/* Booking Details Section */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="time-slot" className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Time Slot *
                </Label>
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger id="time-slot">
                    <SelectValue placeholder="Choose a time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Property Address *</Label>
                <Input
                  id="address"
                  placeholder="Enter your property address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Any special requirements or access instructions..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Pricing Info */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <h4 className="font-semibold">Service Details</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Standard residential gutter cleaning: £80</li>
                  <li>• Average time: 1-2 hours</li>
                  <li>• Includes debris removal and downpipe clearing</li>
                  <li>• Photos provided before and after service</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Book Button */}
          <Button 
            onClick={handleBooking}
            disabled={isSubmitting || !selectedDate || !selectedTime || !address.trim()}
            className="w-full md:w-auto"
            size="lg"
          >
            {isSubmitting ? 'Booking...' : 'Book Appointment'}
          </Button>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Why Regular Gutter Cleaning?</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>✓ Prevents water damage to your property</li>
            <li>✓ Avoids foundation problems</li>
            <li>✓ Reduces pest infestations</li>
            <li>✓ Extends the life of your gutters</li>
            <li>✓ Prevents ice dams in winter</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
